import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import PageWrapper from "../SignupPages/PageWrapper";
import "../SignupPages/SignupCommon.css";
import AlertIcon from "../../assets/images/alert.png";
import CustomCheckoutSignupStepper from "./CheckoutSignupStepper";
import { updateExistingPatient } from "../../services/api";

const CheckoutSignupPage9: React.FC = () => {
  const [feet, setFeet] = useState<string | null>(null);
  const [inches, setInches] = useState<string | null>(null);
  const [height, setHeight] = useState<number>(0);
  const [weight, setWeight] = useState<string>("");
  const [bmi, setBmi] = useState<number | 0>(0);
  const [idealWeight, setIdealWeight] = useState<string>("");
  const [error, setError] = useState<string>("");
  const [isBmiValid, setIsBmiValid] = useState(true);
  const [minValidBmi, setMinValidBmi] = useState(27);
  const [weightTouched, setWeightTouched] = useState(false);
  const [idealWeightTouched, setIdealWeightTouched] = useState(false);
  const [intialLoad, setIntialLoad] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const [addressLine1, setAddressLine1] = useState<string>("");
  const [addressLine2, setAddressLine2] = useState<string>("");
  const [city, setCity] = useState<string>("");
  const [state, setState] = useState<string>("");
  const [zip, setZip] = useState<string>("");
  const [email, setEmail] = useState<string>("");
  const [phone, setPhone] = useState<string>("");
  const [gender, setGender] = useState<string>("");
  const [uuid, setUuid] = useState<string>("");
  const [dob, setDob] = useState<string>("");

  const safeNavigate = (path: string) => {
    if (location.pathname !== path) {
      navigate(path);
    }
  };

  useEffect(() => {
    const step5Completed = localStorage.getItem("step-5-completed");
    if (step5Completed) {
      safeNavigate("/glp1/getting-started/step-6");
    } else {
      safeNavigate("/glp1/getting-started/step-5");
    }
  }, []);

  useEffect(() => {
    const address1 = localStorage.getItem("addressLine1");
    const address2 = localStorage.getItem("addressLine2");
    const city = localStorage.getItem("city");
    const state = localStorage.getItem("state");
    const zip = localStorage.getItem("zip");
    const email = localStorage.getItem("email");
    const phone = localStorage.getItem("phone");
    const uuid = localStorage.getItem("uuid");
    const dob = localStorage.getItem("dob");
    const gender = localStorage.getItem("gender");

    if (address1) {
      setAddressLine1(JSON.parse(address1).value);
    }
    if (address2) {
      setAddressLine2(JSON.parse(address2).value);
    }
    if (city) {
      setCity(JSON.parse(city).value);
    }
    if (state) {
      const parsedState = JSON.parse(state).value;
      setState(parsedState);
    }
    if (zip) {
      setZip(JSON.parse(zip).value);
    }
    if (email) {
      setEmail(JSON.parse(email).value);
    }

    if (phone) {
      setPhone(JSON.parse(phone).value);
    }

    if (uuid) {
      setUuid(JSON.parse(uuid).value);
    }
    if (dob) {
      setDob(JSON.parse(dob).value);
    }
    if (gender) {
      setGender(JSON.parse(gender).value);
    }
  }, []);

  useEffect(() => {
    const storedHeight = localStorage.getItem("height");
    const storedWeight = localStorage.getItem("weight");

    if (storedWeight) {
      try {
        const parsedWeight = JSON.parse(storedWeight);
        setWeight(parsedWeight.value || "");
      } catch (error) {
        console.error("Error parsing weight from localStorage:", error);
      }
    }

    if (storedHeight) {
      try {
        const parsedHeight = JSON.parse(storedHeight);
        const totalHeightInInches = parseInt(parsedHeight.value, 10);
        const feetValue = Math.floor(totalHeightInInches / 12);
        const inchesValue = totalHeightInInches % 12;
        setFeet(feetValue.toString());
        setInches(inchesValue.toString());
        setHeight(totalHeightInInches);
      } catch (error) {
        console.error("Error parsing height from localStorage:", error);
      }
    }
  }, []);

  useEffect(() => {
    const medData = localStorage.getItem("weight_loss_medication");
    if (medData) {
      try {
        const parsed = JSON.parse(medData);
        const isTakingMed = parsed.value === "Yes";
        const threshold = isTakingMed ? 22 : 25;
        setMinValidBmi(threshold);
      } catch (err) {
        console.error("Failed to parse medication data:", err);
      }
    }
  }, []);

  useEffect(() => {
    if (bmi > 0 && minValidBmi > 0) {
      const roundedBmi = parseFloat(bmi.toFixed(1));
      setIsBmiValid(roundedBmi >= minValidBmi);
    }
  }, [bmi, minValidBmi]); // ✅ This ensures latest values are compared

  const calculateBmi = (weight: number, height: number) => {
    if (weight <= 0 || height <= 0) return 0;
    const weightInKg = weight * 0.453592;
    const heightInMeters = height * 0.0254;
    const bmi = weightInKg / (heightInMeters * heightInMeters);
    return Math.min(Math.max(bmi, 0), 200);
  };

  useEffect(() => {
    if (height > 0 && weight) {
      const parsedWeight = parseFloat(weight);
      if (parsedWeight > 0) {
        const newBmi = calculateBmi(parsedWeight, height);
        setBmi(newBmi);
        localStorage.setItem(
          "bmi",
          JSON.stringify({ key: "bmi", value: newBmi })
        );
        setError("");
      }
    }
    const savedIdealWeight = localStorage.getItem("idealWeight");
    if (savedIdealWeight) {
      const parsedIdealWeight = JSON.parse(savedIdealWeight);
      setIdealWeight(parsedIdealWeight.value);
    }
  }, [height, weight]);

  useEffect(() => {
    setIntialLoad(true);
    if (height > 0 && weight) {
      const parsedWeight = parseFloat(weight);
      if (parsedWeight > 0) {
        const newBmi = calculateBmi(parsedWeight, height);
        setBmi(newBmi);
        localStorage.setItem(
          "bmi",
          JSON.stringify({ key: "bmi", value: newBmi })
        );
        setError("");
      }
    }
    const savedIdealWeight = localStorage.getItem("idealWeight");
    if (savedIdealWeight) {
      const parsedIdealWeight = JSON.parse(savedIdealWeight);
      setIdealWeight(parsedIdealWeight.value);
    }
  }, []);

  useEffect(() => {
    if (feet && inches) {
      const feetValue = parseInt(feet, 10);
      const inchesValue = parseInt(inches, 10);
      const totalHeightInInches = feetValue * 12 + inchesValue;
      setHeight(totalHeightInInches);
      localStorage.setItem(
        "height",
        JSON.stringify({ key: "height", value: `${totalHeightInInches}` })
      );

      if (weight) {
        const parsedWeight = parseFloat(weight);
        const newBmi = calculateBmi(parsedWeight, totalHeightInInches);
        setBmi(newBmi);
        localStorage.setItem(
          "bmi",
          JSON.stringify({ key: "bmi", value: newBmi })
        );
      }
    }
  }, [feet, inches]);

  const handleHeightChange = () => {
    if (feet && inches) {
      const feetValue = parseInt(feet, 10);
      const inchesValue = parseInt(inches, 10);
      const totalHeightInInches = feetValue * 12 + inchesValue;
      setHeight(totalHeightInInches);
      localStorage.setItem(
        "height",
        JSON.stringify({ key: "height", value: `${totalHeightInInches}` })
      );

      if (weight) {
        const parsedWeight = parseFloat(weight);
        const newBmi = calculateBmi(parsedWeight, totalHeightInInches);
        setBmi(newBmi);
        localStorage.setItem(
          "bmi",
          JSON.stringify({ key: "bmi", value: newBmi })
        );
      }
    }
  };

  const handleWeightChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setWeightTouched(true);
    setIntialLoad(false);
    setIdealWeightTouched(false);
    const newWeight = e.target.value;
    setWeight(newWeight);

    const parsedNewWeight = parseFloat(newWeight);
    const parsedIdealWeight = parseFloat(idealWeight);

    if (!newWeight || parsedNewWeight <= 0) {
      setError("Weight should be greater than 0.");
      setBmi(0);
      return;
    }

    if (
      idealWeightTouched &&
      !isNaN(parsedIdealWeight) &&
      parsedIdealWeight >= parsedNewWeight
    ) {
      setError("Ideal weight must be less than weight.");
      return;
    }

    setError("");
    localStorage.setItem(
      "weight",
      JSON.stringify({ key: "weight", value: newWeight })
    );

    if (height > 0) {
      const newBmi = calculateBmi(parsedNewWeight, height);
      setBmi(newBmi);
      localStorage.setItem(
        "bmi",
        JSON.stringify({ key: "bmi", value: newBmi })
      );
    }
  };

  const handleIdealWeightChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setIdealWeightTouched(true);
    const newWeight = e.target.value;
    const newWeightFloat = parseFloat(newWeight);
    const currentWeightFloat = parseFloat(weight);

    setIdealWeight(newWeight);

    if (isNaN(newWeightFloat) || newWeightFloat <= 0) {
      setError("Ideal weight should be greater than 0.");
    } else if (
      !isNaN(currentWeightFloat) &&
      newWeightFloat >= currentWeightFloat
    ) {
      setError("Ideal weight must be less than current weight.");
    } else {
      setError("");
    }
  };

  const handleNext = async () => {
    try {
      if (feet && inches) {
        const feetValue = parseInt(feet, 10);
        const inchesValue = parseInt(inches, 10);
        const totalHeightInInches = feetValue * 12 + inchesValue;
        setHeight(totalHeightInInches);
        localStorage.setItem(
          "height",
          JSON.stringify({ key: "height", value: `${totalHeightInInches}` })
        );
      }

      if (idealWeight) {
        localStorage.setItem(
          "idealWeight",
          JSON.stringify({
            key: "idealWeight",
            value: idealWeight,
            id: 18026063,
          })
        );
      }

      const parsedWeight = parseFloat(weight);
      const parsedIdealWeight = parseFloat(idealWeight);

      if (parsedIdealWeight > parsedWeight) {
        setError("Ideal weight must be less than weight.");
        return;
      }

      const roundedBmi = parseFloat(bmi.toFixed(1));
      if (roundedBmi < minValidBmi) {
        setIsBmiValid(false);
        return;
      }

      setIsBmiValid(true);

      const [month, day, year] = dob.split("/");
      const formattedDobForApi = `${month}/${day}/${year}`;
      const productCategory = "GLP1";

      const response = await updateExistingPatient(
        email,
        formattedDobForApi,
        gender,
        height,
        weight,
        city,
        addressLine1,
        addressLine2,
        state,
        zip,
        phone,
        productCategory,
        uuid
      );

      console.log(response?.message);
      localStorage.setItem("step-6-completed", "true");
      safeNavigate("/glp1/getting-started/step-7");
    } catch (err: unknown) {
      if (err instanceof Error) {
        setError(err.message);
      } else {
        setError("Something went wrong while updating the patient.");
      }
    }
  };

  const feetOptions = Array.from({ length: 8 }, (_, i) => ({
    value: (i + 1).toString(),
    label: `${i + 1} ft`,
  }));

  const inchOptions = Array.from({ length: 12 }, (_, i) => ({
    value: i.toString(),
    label: `${i} in`,
  }));

  const handleBack = () => {
    safeNavigate("/glp1/getting-started/step-5");
  };

  return (
    <PageWrapper onBack={handleBack}>
      <div className="signup-page-container container">
        <CustomCheckoutSignupStepper activeStep={1} onBack={handleBack} />
        <div className="signup-content">
          <p className="signup-content-header" style={{ marginBottom: "29px" }}>
            What is your current height & weight?
          </p>
          <p
            className="signup-content-heading"
            style={{ marginBottom: "10px" }}
          >
            Height (feet & inches) *
          </p>
          <div style={{ display: "flex", gap: "20px", marginBottom: "20px" }}>
            <div className="custom-select-wrapper">
              <select
                value={feet || ""}
                onChange={(e) => {
                  setFeet(e.target.value);
                  handleHeightChange();
                }}
                className="custom-select"
              >
                <option value="" disabled>
                  Select feet
                </option>
                {feetOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="custom-select-wrapper">
              <select
                value={inches || ""}
                onChange={(e) => {
                  setInches(e.target.value);
                  handleHeightChange();
                }}
                className="custom-select"
              >
                <option value="" disabled>
                  Select inches
                </option>
                {inchOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <p
            className="signup-content-heading"
            style={{ marginBottom: "10px" }}
          >
            Weight (lbs) *
          </p>
          <input
            id="weight-input"
            type="number"
            value={weight}
            onChange={handleWeightChange}
            onBlur={() => {
              setWeightTouched(true);
              setIdealWeightTouched(true);
            }}
            placeholder="In lbs"
            className="weight-input"
            inputMode="numeric"
            min="0"
          />
          <div className="bmi-container">
            <p className="bmi-content">{`Your BMI Score is ${bmi.toFixed(
              1
            )}`}</p>
          </div>
          {((idealWeightTouched && bmi > 0 && !isBmiValid && weightTouched) ||
            (bmi > 0 && !isBmiValid && intialLoad)) && (
            <div
              className="bmi-error-container"
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                gap: "10px",
                padding: "10px",
                height: "50px",
                color: "#f8454a",
                borderRadius: "48px",
                marginTop: "20px",
              }}
            >
              <img src={AlertIcon} width="16px" height="16px" />
              <p
                className="bmi-content"
                style={{ color: "#f8454a", lineHeight: "18px" }}
              >
                Our doctors can only prescribe if your BMI is {minValidBmi} or
                higher.
              </p>
            </div>
          )}

          <p
            className="signup-content-heading"
            style={{ marginBottom: "10px", marginTop: "20px" }}
          >
            Enter your ideal weight you want to achieve *
          </p>
          <input
            id="ideal-weight-input"
            type="number"
            value={idealWeight}
            onChange={handleIdealWeightChange}
            // onFocus={() => setIdealWeightTouched(true)}
            placeholder="In lbs"
            className="weight-input"
            inputMode="numeric"
            min="0"
          />

          {weightTouched && idealWeightTouched && error && (
            <div
              className="bmi-error-container"
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                gap: "10px",
                padding: "10px",
                height: "50px",
                color: "#f8454a",
                borderRadius: "48px",
                marginTop: "20px",
              }}
            >
              <img src={AlertIcon} width="16px" height="16px" />
              <p
                className="bmi-content"
                style={{ color: "#f8454a", lineHeight: "18px" }}
              >
                {error}
              </p>
            </div>
          )}

          <button
            className="next-btn sticky-btn"
            disabled={
              !feet ||
              !inches ||
              !isBmiValid ||
              !weight ||
              !idealWeight ||
              !!error
            }
            onClick={handleNext}
          >
            Next →
          </button>
        </div>
      </div>
    </PageWrapper>
  );
};

export default CheckoutSignupPage9;
